import React from 'react';
import { useJwt } from '../context/JwtContext';
import RadixButton from './ui/RadixButton';
import RadixDialog from './ui/RadixDialog';
import { ClipboardCopyIcon } from '@radix-ui/react-icons';

/**
 * JWT Generation Dialog Component
 * @returns {JSX.Element} JwtDialog component
 */
const JwtDialog = () => {
  const {
    jwtDialogOpen,
    setJwtDialogOpen,
    userId,
    setUserId,
    jwt,
    jwtLoading,
    jwtError,
    handleCloseJwtDialog,
    handleGenerateJwt,
    handleUserIdKeyDown,
    copyToClipboard
  } = useJwt();

  return (
    <RadixDialog
      open={jwtDialogOpen}
      onOpenChange={setJwtDialogOpen}
      title="Generate JWT Token"
      description="Enter a user ID to generate a JWT token for that user."
      footer={
        <>
          <RadixButton
            variant="outline"
            onClick={handleCloseJwtDialog}
          >
            Cancel
          </RadixButton>
          <RadixButton
            variant="primary"
            onClick={handleGenerateJwt}
            disabled={jwtLoading}
          >
            {jwtLoading ? 'Generating...' : 'Generate'}
          </RadixButton>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label htmlFor="userId" className="block text-sm font-medium text-gray-700 mb-1">
            User ID
          </label>
          <input
            id="userId"
            type="number"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            onKeyDown={handleUserIdKeyDown}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter user ID"
            disabled={jwtLoading}
            autoFocus
          />
        </div>

        {jwtError && (
          <div className="text-red-500 text-sm">{jwtError}</div>
        )}

        {jwt && (
          <div className="mt-4">
            <div className="flex justify-between items-center mb-1">
              <label className="block text-sm font-medium text-gray-700">JWT Token</label>
              <button
                onClick={() => copyToClipboard(jwt)}
                className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
              >
                <ClipboardCopyIcon className="w-4 h-4 mr-1" />
                Copy
              </button>
            </div>
            <div className="bg-gray-50 p-3 rounded-md border border-gray-200 overflow-auto max-h-40">
              <pre className="text-xs whitespace-pre-wrap break-all">{jwt}</pre>
            </div>
          </div>
        )}
      </div>
    </RadixDialog>
  );
};

export default JwtDialog;
